/* Calculator: Contains logic for calculator and data for Calculator Cache */
import { actionTypes } from '../actions/types';
import LoanProductURL from '../util/mappings/loanProductURL';
import LoanType from '../util/mappings/loanType';

type AppActions = import('../actions/types').AppActions;

interface InterestsAndFeesWeb {
    effInterestMaxEquity: number | undefined;
    effInterestMidEquity: number | undefined;
    effInterestMinEquity: number | undefined;
    equityPercentThresholdMaxInterest: number | undefined;
    equityPercentThresholdMidInterest: number | undefined;
    establishFee: number | undefined;
    loanAmountMax: number;
    loanAmountMin: number;
    loanProduct: string;
    maxAge?: number | null;
    nomInterestMaxEquity: number | undefined;
    nomInterestMidEquity: number | undefined;
    nomInterestMinEquity: number | undefined;
    objectPriceDef: number | undefined;
    pledgeFee: number | undefined;
    pledgeFeeManual: number | undefined;
    priceStep: number | undefined;
    termFee: number | undefined;
}

export interface RepaymentPeriodTermsWeb {
    defTerm: number;
    id?: number | null;
    maxTerms: number;
    possibleTerms: number[];
}

export interface CalculatorReducer {
    isLoan: boolean;
    isLoanCommitment: boolean;

    isCar: boolean;
    isCarOrdinary: boolean;
    isCarElectric: boolean;
    isCarNoInsurance: boolean;
    isMC: boolean;
    isATV: boolean;
    isSnowmobile: boolean;
    isCampingCar: boolean;
    isCampingCaravan: boolean;
    isBoat: boolean;

    objectAge: number;
    isObjectAgeEligible: boolean;
    isObjectZeroEmission: boolean;

    product: {
        interestsAndFees: InterestsAndFeesWeb;
        repaymentPeriodTerms: RepaymentPeriodTermsWeb;
    };

    loanInfo: {
        vehicleURL: string;
        loanProduct: string;
        loanType: string | null | undefined;
        vehicleRegistrationNumber?: string | null | undefined;
        odometerValue?: number | null | undefined;
        loanAmount: number | null | undefined;
        loanAmountWithoutFees: number | null | undefined;
        pledgeFee: number | null | undefined;
        equity: number | null | undefined;
        vehiclePrice: number | null | undefined;
        requestedLoanPeriodInMonths: number | null | undefined;
        effectiveInterest: number | null | undefined;
        nominalInterest: number | null | undefined;
        invoiceFee: number | null | undefined;
        monthlyCost: number | null | undefined;
        startupFee: number | null | undefined;
        totalCost: number | null | undefined;
    };
}

const initialState: CalculatorReducer = {
    isLoan: true, // default: true
    isLoanCommitment: false,

    isCar: false,
    isCarOrdinary: false,
    isCarElectric: false,
    isCarNoInsurance: false,
    isMC: false,
    isATV: false,
    isSnowmobile: false,
    isCampingCar: false,
    isCampingCaravan: false,
    isBoat: false,

    objectAge: 0,
    isObjectAgeEligible: true,
    isObjectZeroEmission: true,

    product: {
        interestsAndFees: {
            effInterestMaxEquity: undefined,
            effInterestMidEquity: undefined,
            effInterestMinEquity: undefined,
            equityPercentThresholdMaxInterest: undefined,
            equityPercentThresholdMidInterest: undefined,
            establishFee: undefined,
            loanAmountMax: 100000,
            loanAmountMin: 30000,
            loanProduct: '',
            maxAge: null,
            nomInterestMaxEquity: undefined,
            nomInterestMidEquity: undefined,
            nomInterestMinEquity: undefined,
            objectPriceDef: undefined,
            pledgeFee: undefined,
            pledgeFeeManual: undefined,
            priceStep: undefined,
            termFee: undefined,
        },
        repaymentPeriodTerms: {
            defTerm: 36,
            id: null,
            maxTerms: 120,
            possibleTerms: [],
        },
    },

    loanInfo: {
        vehicleURL: '',
        loanProduct: '',
        loanType: LoanType.loan, // Default: Loan
        vehicleRegistrationNumber: undefined,
        odometerValue: 0,
        loanAmount: undefined,
        loanAmountWithoutFees: undefined,
        pledgeFee: undefined,
        equity: 0,
        vehiclePrice: undefined,
        requestedLoanPeriodInMonths: undefined,
        effectiveInterest: undefined,
        nominalInterest: undefined,
        invoiceFee: undefined,
        monthlyCost: undefined,
        startupFee: undefined,
        totalCost: undefined,
    },
};

const getLoanType = (loanType: string) => ({
    isLoan: loanType === LoanType.loan,
    isLoanCommitment: loanType === LoanType.loanCommitment,
});

const getProductType = (vehicleURL: string) => ({
    isCar:
        vehicleURL === LoanProductURL.car ||
        vehicleURL === LoanProductURL.carElectric ||
        vehicleURL === LoanProductURL.carNoInsurance,
    isCarOrdinary: vehicleURL === LoanProductURL.car,
    isCarElectric: vehicleURL === LoanProductURL.carElectric,
    isCarNoInsurance: vehicleURL === LoanProductURL.carNoInsurance,
    isMC: vehicleURL === LoanProductURL.mc,
    isATV: vehicleURL === LoanProductURL.atv,
    isSnowmobile: vehicleURL === LoanProductURL.snowmobile,
    isCampingCar: vehicleURL === LoanProductURL.campingCar,
    isCampingCaravan: vehicleURL === LoanProductURL.campingCaravan,
    isBoat: vehicleURL === LoanProductURL.boat,
});

export const calculatorReducer = (state = initialState, action: AppActions = {} as AppActions): CalculatorReducer => {
    switch (action.type) {
        /* Get Product Info (interest rates, etc.) */
        case actionTypes.FETCH_PRODUCT_INFO_FULFILLED: {
            return {
                ...state,
                product: {
                    ...state.product,
                    ...action.payload,
                },
                loanInfo: {
                    ...state.loanInfo,
                    loanProduct: action.payload.interestsAndFees.loanProduct,
                },
            };
        }

        /* Update Product */
        case actionTypes.UPDATE_PRODUCT: {
            const vehicleURL = action.payload;
            return {
                ...state,
                ...getProductType(vehicleURL),
                loanInfo: {
                    ...state.loanInfo,
                    vehicleURL,
                },
            };
        }

        /* Set correct Loan Type (Loan or Commitment) */
        case actionTypes.UPDATE_LOAN_TYPE: {
            const loanType = action.payload;
            return {
                ...state,
                ...getLoanType(loanType),
                loanInfo: {
                    ...state.loanInfo,
                    loanType,
                },
            };
        }

        /* Fetch Vehicle */
        case actionTypes.FETCH_VEHICLE_INFO_FULFILLED: {
            const fuel = action.payload?.vehicleDetails?.fuel;
            const manufactureYear = action.payload?.vehicleDetails?.manufactureYear;
            const objectAge = manufactureYear ? new Date().getFullYear() - manufactureYear : 0;
            const { maxAge } = state.product.interestsAndFees;
            const isObjectZeroEmission = fuel === 'ELEKTRISITET' || fuel === 'HYDROGEN';
            return {
                ...state,
                loanInfo: {
                    ...state.loanInfo,
                    requestedLoanPeriodInMonths: undefined, // Reset Loan Period when new vehicle is fetched
                },
                objectAge,
                isObjectAgeEligible: (maxAge && objectAge < maxAge) || state.isCarNoInsurance,
                isObjectZeroEmission,
            };
        }
        case actionTypes.FETCH_VEHICLE_INFO_FAILED: {
            return {
                ...state,
                objectAge: initialState.objectAge,
                isObjectAgeEligible: initialState.isObjectAgeEligible,
                isObjectZeroEmission: initialState.isObjectZeroEmission,
            };
        }

        /* Form: Vehicle Info */
        case actionTypes.FORM_VEHICLE_INFO: {
            const { vehicleRegistrationNumber, odometerValue } = action.payload;
            return {
                ...state,
                loanInfo: {
                    ...state.loanInfo,
                    vehicleRegistrationNumber,
                    odometerValue,
                },
            };
        }

        /* Form: Loan Info */
        case actionTypes.FORM_LOAN_INFO: {
            const { vehiclePrice, equity, requestedLoanPeriodInMonths } = action.payload;
            const loanAmountWithoutFees = vehiclePrice !== undefined && equity !== undefined && vehiclePrice - equity;
            return {
                ...state,
                loanInfo: {
                    ...state.loanInfo,
                    loanAmountWithoutFees: loanAmountWithoutFees || state.loanInfo.loanAmountWithoutFees,
                    equity,
                    requestedLoanPeriodInMonths,
                    vehiclePrice,
                },
            };
        }

        /* Re-introduce calculator state from a cacheId param cache lookup. */
        case actionTypes.FETCH_CALCULATOR_CACHE_FULFILLED: {
            const {
                vehicleURL,
                loanType,
                loanAmount,
                loanProduct,
                vehiclePrice,
                equity,
                requestedLoanPeriodInMonths,
                vehicleRegistrationNumber,
                odometerValue,
            } = action.payload;

            return {
                ...state,
                ...getProductType(vehicleURL || ''),
                ...getLoanType(loanType || ''),
                loanInfo: {
                    ...state.loanInfo,
                    vehicleURL: vehicleURL || '',
                    loanType: loanType || '',
                    loanAmountWithoutFees: loanAmount || 0,
                    loanProduct: loanProduct || '',
                    vehiclePrice: vehiclePrice || 0,
                    equity: equity || 0,
                    requestedLoanPeriodInMonths: requestedLoanPeriodInMonths || 0,
                    vehicleRegistrationNumber: vehicleRegistrationNumber || '',
                    odometerValue: odometerValue || 0,
                },
            };
        }

        case actionTypes.SEND_APPLICATION_FULFILLED:
        case actionTypes.FETCH_APPLICATION_FULFILLED:
        case actionTypes.UPDATE_APPLICATION_FULFILLED: {
            const loanInfo = action.payload?.loanInfo;
            return {
                ...state,
                loanInfo: {
                    ...state.loanInfo,
                    ...loanInfo,
                },
            };
        }

        default:
            return state;
    }
};
